import { Assignment, ClassSidebarVisibility, TeacherClass } from "../../../data-types/Types";

export const AUTO_ARCHIVE_AFTER_MONTHS = 10;

export type ClassSidebarPlacement = "active" | "inactive";

type DateLike = Date | number | string | { toMillis?: () => number; seconds?: number } | undefined;

const toUtcMillis = (value: DateLike): number | undefined => {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  if (value && typeof value.toMillis === "function") {
    return value.toMillis();
  }
  if (value && typeof value.seconds === "number") {
    return value.seconds * 1000;
  }
  return undefined;
};

export const getAutomaticArchiveCutoffUtcMs = (nowUtcMs = Date.now()): number => {
  const cutoff = new Date(nowUtcMs);
  const originalDay = cutoff.getUTCDate();
  cutoff.setUTCDate(1);
  cutoff.setUTCMonth(cutoff.getUTCMonth() - AUTO_ARCHIVE_AFTER_MONTHS);
  const finalDayOfMonth = new Date(Date.UTC(
    cutoff.getUTCFullYear(),
    cutoff.getUTCMonth() + 1,
    0
  )).getUTCDate();
  cutoff.setUTCDate(Math.min(originalDay, finalDayOfMonth));
  return cutoff.getTime();
};

export const getLatestClassScheduleUtcMs = (
  teacherClass: TeacherClass,
  assignments: Assignment[]
): number | undefined => {
  const candidateDates: DateLike[] = [teacherClass.createdOn];
  assignments.forEach((assignment) => {
    candidateDates.push(
      assignment.createdOn,
      assignment.lastEdited,
      assignment.startAtUtcMs,
      assignment.endAtUtcMs
    );
  });

  const timestamps = candidateDates
    .map(toUtcMillis)
    .filter((value): value is number => value !== undefined);
  return timestamps.length > 0 ? Math.max(...timestamps) : undefined;
};

export const getClassSidebarPlacement = (
  teacherClass: TeacherClass,
  assignments: Assignment[],
  nowUtcMs = Date.now()
): ClassSidebarPlacement => {
  const visibility: ClassSidebarVisibility = teacherClass.sidebarVisibility || "automatic";
  if (visibility === "always-visible") {
    return "active";
  }
  if (visibility === "manually-archived") {
    return "inactive";
  }

  const latestScheduleUtcMs = getLatestClassScheduleUtcMs(teacherClass, assignments);
  if (latestScheduleUtcMs === undefined) {
    return "active";
  }
  return latestScheduleUtcMs <= getAutomaticArchiveCutoffUtcMs(nowUtcMs)
    ? "inactive"
    : "active";
};
